// noinspection SuspiciousTypeOfGuard

import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import SearchIcon from "@mui/icons-material/Search";
import { styled, useTheme } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormLabel from "@mui/material/FormLabel";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useDialog } from "_shared/_providers/dialogs/dialog";
import { useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import { addSpacesToTitleCase } from "_shared/_utilities/add-spaces-to-title-case";
import LoadingButton from "_shared/_views/loading/loading-button";
import {
  getEntityMembers,
  getEntityMembersError,
  getEntityMembersIsLoading,
  populateEntityMembersByLegalEntityId,
} from "_shared/entity-members/_stores/entity-members-redux-slice";
import EntityMember from "_shared/entity-members/entity-member";
import Guid from "_shared/guids/guid";
import axios from "axios";
import { enqueueSnackbar } from "notistack";
import { addPermission } from "permissions/_stores/permissions-redux-slice";
import SplitSearch, { SearchOption } from "permissions/_views/tables/_components/split-search";
import { DocumentAccess } from "permissions/resources/documents/documents-permissions-resources";
import { EntityAgreementAccess } from "permissions/resources/legal-entities/legal-entities-permissions-resources";
import { ForumAccess } from "permissions/resources/messaging/messaging-permissions-resources";
import type { Resource } from "permissions/resources/permission-resource";
import { AccessLevel, ResourceConstructor } from "permissions/resources/types";
import { WorkAgreementField } from "permissions/resources/work/types";
import { ProposalAccess, WorkAgreementAccess } from "permissions/resources/work/work-permissions-resources";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useSession } from "users/__session/session-context";
import Permission from "../../permission";
import { getIndividualsByUserIds } from "marketplace/individuals/_stores/individuals-redux-slice";

// Styles
const AddPermissionContainer = styled("div")(() => ({
  display: "flex",
  height: "auto",
  width: "100%",
}));
const SelectContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("sm")]: {
    gridTemplateColumns: "repeat(2, 1fr)",
    gridTemplateRows: "repeat(4, auto)",
  },
  display: "grid",
  columnGap: theme.spacing(4),
  rowGap: theme.spacing(2),
  gridTemplateColumns: "1fr auto min-content",
  gridTemplateRows: "1fr 1fr",
  marginBottom: theme.spacing(1),
  width: "100%",
}));
const UserChip = styled(Chip)(({ theme }) => ({
  backgroundColor: "rgba(200, 225, 255, 0.9)",
  color: theme.palette.common.black,
  marginBottom: theme.spacing(0.5),
  width: "fit-content",
}));
const UsersFormControl = styled(FormControl)(({ theme }) => ({
  [theme.breakpoints.down("sm")]: {
    gridColumn: "1 / span 2",
  },
  gridColumn: 1,
  gridRow: 1,
}));
const UsersFormControlLabel = styled(FormControlLabel)(() => ({
  alignItems: "stretch",
  margin: 0,
}));
const UsersTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      borderColor: "#777",
    },
    "&:hover fieldset": {
      borderColor: theme.palette.primary.main,
    },
  },
}));
const TargetControl = styled(SplitSearch)(({ theme }) => ({
  [theme.breakpoints.down("sm")]: {
    gridColumn: "1 / span 2",
  },
  gridColumn: 1,
  gridRow: 2,
}));
const FieldsFormControl = styled(FormControl)(({ theme }) => ({
  [theme.breakpoints.down("sm")]: {
    gridColumn: "1 / span 2",
    gridRow: 3,
  },
  gridColumn: 2,
  gridRow: "1 / span 2",
  width: "100%",
  "& .MuiFormLabel-root.Mui-focused": {
    color: theme.palette.text.primary,
  },
}));
const FieldsContainer = styled(Box)(() => ({
  display: "grid",
  gridTemplateColumns: "min-content max-content auto",
  alignItems: "center",
}));
const FieldContainer = styled(Box)(() => ({
  display: "grid",
  gridTemplateColumns: "repeat(2, min-content)",
  gap: 0.25,
  maxHeight: "100px",
  overflowY: "auto",
}));
const AccessFieldCellLabel = styled(Typography)(({ theme }) => ({
  fontSize: "1em",
  fontWeight: 500,
  marginRight: theme.spacing(1),
  textAlign: "right",
  whiteSpace: "nowrap",
}));
const AccessFieldCellValue = styled(Typography)(() => ({
  fontSize: "1em",
  fontWeight: 300,
}));
const AccessFormControl = styled(FormControl)(({ theme }) => ({
  [theme.breakpoints.down("sm")]: {
    gridColumn: 1,
    gridRow: 3,
  },
  gridColumn: 2,
  gridRow: "1 / span 2",
  "& .MuiFormLabel-root.Mui-focused": {
    color: theme.palette.text.primary,
  },
}));
const ButtonContainer = styled("div")(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(1),
  justifyContent: "flex-end",
  marginTop: theme.spacing(2),
}));
const AddButtonContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("sm")]: {
    gridColumn: 1,
    gridRow: 5,
  },
  alignContent: "end",
  gridColumn: 3,
  gridRow: "1 / span 2",
  paddingBottom: theme.spacing(1.5),
}));
const CancelButton = styled(Button)(({ theme }) => ({
  color: theme.palette.text.primary,
}));

// Types
type PermissionChange = {
  grantees: EntityMember[];
  resource: Resource;
};

export default function AddPermissionDialog() {
  // State
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [selectedResource, setSelectedResource] = useState<Resource | null>(null);
  const [selectedEntityMembers, setSelectedEntityMembers] = useState<EntityMember[]>([]);
  const [accessLevel, setAccessLevel] = useState<AccessLevel>(AccessLevel["Read/Write"]);
  const [pendingChanges, setPendingChanges] = useState<PermissionChange[]>([]);
  const [fields, setFields] = useState<Record<WorkAgreementField, AccessLevel>>({
    [WorkAgreementField.ClientTeam]: AccessLevel["Read/Write"],
    [WorkAgreementField.Conflicts]: AccessLevel["Read/Write"],
    [WorkAgreementField.Currency]: AccessLevel["Read/Write"],
    [WorkAgreementField.Discount]: AccessLevel["Read/Write"],
    [WorkAgreementField.FeeSchedule]: AccessLevel["Read/Write"],
    [WorkAgreementField.Invoices]: AccessLevel["Read/Write"],
    [WorkAgreementField.Policies]: AccessLevel["Read/Write"],
    [WorkAgreementField.VendorTeam]: AccessLevel["Read/Write"],
  });

  // Hooks
  const theme = useTheme();
  const session = useSession();
  const { closeDialog } = useDialog();
  const dispatch = useAttorneyHubDispatch();

  const entityMemberList = useSelector(getEntityMembers(session.user!.legalEntityId));
  const isLoadingEntityMemberList = useSelector(getEntityMembersIsLoading(session.user!.legalEntityId));
  const errorLoadingEntityMemberList = useSelector(getEntityMembersError(session.user!.legalEntityId));

  const entityMemberUserIds = entityMemberList?.map((member) => member.userId) ?? [];
  const entityMemberIndividuals = useSelector(getIndividualsByUserIds(entityMemberUserIds));
  
  // Lifecycle
  useEffect(() => {
    if (errorLoadingEntityMemberList) {
      enqueueSnackbar("Unable to load entity members", { variant: "error" });
    } else if (!isLoadingEntityMemberList && entityMemberList === undefined) {
      dispatch(populateEntityMembersByLegalEntityId({ session, legalEntityId: session.user!.legalEntityId }));
    }
  }, [dispatch, entityMemberList, errorLoadingEntityMemberList, isLoadingEntityMemberList, session]);

  /**
   * Saves all pending permission changes by calling the Permissions API.
   * Iterates over each pending change and its grantees to create and save Permission instances.
   */
  async function saveChanges(): Promise<void> {
    if (!pendingChanges.length) {
      console.warn("No pending changes to save");
      return;
    }

    try {
      setIsSaving(true);

      for (const change of pendingChanges) {
        for (const member of change.grantees) {
          const permission = new Permission({
            resource: change.resource,
            entityId: session.user!.legalEntityId,
            granteeUserId: member.userId,
            grantorUserId: session.user!.legalEntityId
          });
          dispatch(addPermission({ session, permission }));
        }
      }

      setPendingChanges([]);
      setSelectedEntityMembers([]);
      setSelectedResource(null);

      enqueueSnackbar("Permission(s) saved", { variant: "success" });

      closeDialog();
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.data) {
        enqueueSnackbar("Unable to save Permission: " + error.response.data, {
          variant: "warning",
        });
      } else {
        console.error(error);
        enqueueSnackbar("Unable to save Permission", { variant: "error" });
      }
    } finally {
      setIsSaving(false);
    }
  }

  // Handlers

  /**
   * Handles the selection of a target resource from the SplitSearch component.
   * Creates the appropriate Resource instance based on the selected option.
   * @param searchOption The selected search option
   */
  function handleTargetSelected(searchOption: SearchOption | null) {
    if (!searchOption) {
      setSelectedResource(null);
      return;
    }

    switch (searchOption.kind) {
      case "DocumentAccess": {
        const document = new DocumentAccess({
          documentId: new Guid(searchOption.id)
        });
        setSelectedResource(document);
        break;
      }
      case "ProposalAccess": {
        const proposal = new ProposalAccess({
          proposalId: new Guid(searchOption.id)
        });
        setSelectedResource(proposal);
        break;
      }
      case "WorkAgreementAccess": {
        const workAgreement = new WorkAgreementAccess({
          workAgreementId: new Guid(searchOption.id)
        });
        setSelectedResource(workAgreement);
        break;
      }
      case "EntityAgreementAccess": {
        const entityAgreementAccess = new EntityAgreementAccess({
          entityAgreementId: new Guid(searchOption.id)
        });
        setSelectedResource(entityAgreementAccess);
        break;
      }
      case "ForumAccess": {
        const forumAccess = new ForumAccess({
          forumId: new Guid(searchOption.id)
        });
        setSelectedResource(forumAccess);
        break;
      }
      default: {
        setSelectedResource(null);
        break;
      }
    }
  }

  /**
   * Handles the addition of a new pending permission change.
   * Validates that a resource and at least one entity member is selected.
   * Merges with existing pending change if the resource already exists.
   */
  function handleAddClicked() {
    if (!selectedResource || !selectedEntityMembers) {
      throw new Error("No resource or entity member(s) selected");
    }

    const ResourceClass = selectedResource.constructor as ResourceConstructor;
    let updatedResource = new ResourceClass({ ...selectedResource });

    if (selectedResource instanceof ProposalAccess || selectedResource instanceof WorkAgreementAccess) {
      updatedResource = new ResourceClass({
        ...selectedResource,
        fields: { ...fields },
      });
    }

    if ("accessLevel" in selectedResource) {
      updatedResource = new ResourceClass({
        ...selectedResource,
        accessLevel: Number(accessLevel),
      });
    }

    setSelectedResource(updatedResource);

    // Add the pending change if the resource doesn't already exist in pendingChanges
    // If it does, merge the new selectedEntityMembers into the existing change
    setPendingChanges((prev) => {
      const existingChange = prev.find((change) => change.resource.toJSON() === updatedResource.toJSON());
      if (existingChange) {
        existingChange.grantees = Array.from(new Set([...existingChange.grantees, ...selectedEntityMembers]));
        return [...prev];
      }
      return [
        ...prev,
        {
          resource: updatedResource,
          grantees: selectedEntityMembers,
        },
      ];
    });
  }

  /**
   * Handles the removal of a pending permission change.
   * @param pendingChange The pending change to remove
   */
  function handleRemoveClicked(pendingChange: PermissionChange) {
    setPendingChanges((prev) => prev.filter((p) => p !== pendingChange));
  }

  return (
    <React.Fragment>
      <AddPermissionContainer>
        <SelectContainer>
          <UsersFormControl>
            <UsersFormControlLabel
              control={
                <Autocomplete
                  multiple
                  options={entityMemberList}
                  getOptionLabel={(member) =>
                    entityMemberIndividuals.find((individual) => individual.userId.isEqualTo(member.userId))?.fullName ?? ""
                  }
                  renderInput={(params) => (
                    <UsersTextField
                      {...params}
                      placeholder="Search Users"
                      slotProps={{
                        input: {
                          ...params.slotProps.input,
                          startAdornment: (
                            <React.Fragment>
                              <InputAdornment position="start">
                                <SearchIcon />
                              </InputAdornment>
                              {params.slotProps.input?.startAdornment}
                            </React.Fragment>
                          ),
                        },
                      }}
                    />
                  )}
                  value={selectedEntityMembers}
                  onChange={(_event, newValue) => {
                    setSelectedEntityMembers(newValue);
                  }}
                />
              }
              label="Users"
              labelPlacement="top"
              slotProps={{
                typography: {
                  sx: {
                    fontWeight: "500",
                    marginBottom: 1,
                    marginRight: "auto",
                  },
                },
              }}
            />
          </UsersFormControl>
          <TargetControl
            disabled={!selectedEntityMembers.length}
            sx={{
              opacity: selectedEntityMembers.length ? 1 : 0.5,
              pointerEvents: selectedEntityMembers.length ? "auto" : "none",
            }}
            onTargetSelected={handleTargetSelected}
          />
          {selectedResource instanceof ProposalAccess || selectedResource instanceof WorkAgreementAccess ? (
            <FieldsFormControl
              disabled={!selectedResource || !selectedEntityMembers.length}
              sx={{
                opacity: selectedResource && selectedEntityMembers.length ? 1 : 0.5,
              }}
            >
              <FormLabel
                id="fields-label"
                sx={{
                  color: theme.palette.text.primary,
                  fontWeight: "500",
                }}
              >
                Fields
              </FormLabel>
              <FieldsContainer>
                {Object.values(WorkAgreementField)
                  .filter((field) => typeof field !== "string")
                  .map((field: WorkAgreementField) => {
                    const isDisabled: boolean = typeof fields[field] !== "number";
                    const isReadWrite: boolean = fields[field] === AccessLevel["Read/Write"];

                    return (
                      <React.Fragment key={field}>
                        <Checkbox
                          size="small"
                          edge="start"
                          checked={!isDisabled}
                          onChange={(_event, checked) => {
                            setFields((prev) => {
                              const updated = { ...prev };
                              if (checked) updated[field] = AccessLevel["Read/Write"];
                              else delete updated[field];
                              return updated;
                            });
                          }}
                        />
                        <Typography
                          sx={{
                            color: theme.palette.text.primary,
                            fontWeight: "500",
                            opacity: isDisabled ? 0.5 : 1,
                          }}
                        >
                          {addSpacesToTitleCase(WorkAgreementField[field])}
                        </Typography>
                        <FormControlLabel
                          sx={{ ml: 0.5 }}
                          value={AccessLevel["Read/Write"]}
                          control={
                            <Switch
                              disabled={isDisabled}
                              size="small"
                              color="primary"
                              sx={{ ml: 1 }}
                              checked={isReadWrite}
                              onChange={(_event, checked) =>
                                setFields((prev) => ({
                                  ...prev,
                                  [field]: checked ? AccessLevel["Read/Write"] : AccessLevel["Read-Only"],
                                }))
                              }
                            />
                          }
                          label={(() => {
                            if (isDisabled) return "Disabled";
                            return isReadWrite ? "Read/Write" : "Read-Only";
                          })()}
                          labelPlacement="end"
                          slotProps={{
                            typography: {
                              sx: {
                                fontSize: "0.9rem",
                                fontWeight: isReadWrite ? "500" : "400",
                              },
                            },
                          }}
                        />
                      </React.Fragment>
                    );
                  })}
              </FieldsContainer>
            </FieldsFormControl>
          ) : (
            <AccessFormControl
              disabled={!selectedResource || !selectedEntityMembers.length}
              sx={{
                opacity: selectedResource && selectedEntityMembers.length ? 1 : 0.5,
              }}
            >
              <FormLabel
                id="access-label"
                sx={{
                  color: theme.palette.text.primary,
                  fontWeight: "500",
                }}
              >
                Access
              </FormLabel>
              <RadioGroup
                aria-labelledby="access-label"
                name="access"
                value={accessLevel}
                onChange={(event) => setAccessLevel(event.target.value as unknown as AccessLevel)}
              >
                <FormControlLabel value={AccessLevel["Read-Only"]} control={<Radio />} label="Read-Only" />
                <FormControlLabel value={AccessLevel["Read/Write"]} control={<Radio />} label="Read/Write" />
              </RadioGroup>
            </AccessFormControl>
          )}
          <AddButtonContainer>
            <Tooltip
              title={
                !selectedResource || !selectedEntityMembers || selectedEntityMembers.length === 0
                  ? "Select a resource and at least one user"
                  : "Add Permission"
              }
            >
              <span>
                <Button
                  variant="outlined"
                  color="primary"
                  disabled={!selectedResource || !selectedEntityMembers || selectedEntityMembers.length === 0}
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={handleAddClicked}
                >
                  Add
                </Button>
              </span>
            </Tooltip>
          </AddButtonContainer>
        </SelectContainer>
      </AddPermissionContainer>
      <TableContainer sx={{ maxHeight: 300 }}>
        <Table stickyHeader sx={{ opacity: pendingChanges.length ? 1 : 0.5 }}>
          <TableHead>
            <TableRow>
              <TableCell>Users</TableCell>
              <TableCell>Target</TableCell>
              <TableCell>Access</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {!pendingChanges.length && (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                  <Typography>No pending changes</Typography>
                </TableCell>
              </TableRow>
            )}
            {pendingChanges.map((pendingChange: PermissionChange, index: number) => (
              <TableRow key={pendingChange.resource.toString() ?? index}>
                <TableCell sx={{ pl: 0 }}>
                  <Stack>
                    {pendingChange.grantees.map((entityMember) => (
                      <UserChip
                        key={entityMember.userId.value}
                        size="small"
                        label={
                          entityMemberIndividuals.find((individual) => individual.userId.isEqualTo(entityMember.userId))
                            ?.fullName ?? "Unknown User"
                        }
                      />
                    ))}
                  </Stack>
                </TableCell>
                <TableCell>
                  <Typography>{pendingChange.resource?.toString()}</Typography>
                </TableCell>
                <TableCell>
                  {"accessLevel" in pendingChange.resource
                    ? AccessLevel[pendingChange.resource.accessLevel as keyof typeof AccessLevel]
                    : null}
                  {"fields" in pendingChange.resource && pendingChange.resource.fields ? (
                    <FieldContainer>
                      {Object.values(WorkAgreementField)
                        .filter((field) => typeof field !== "string")
                        .map((field: WorkAgreementField) => {
                          const value = (pendingChange.resource as WorkAgreementAccess).fields?.[field];
                          let label = WorkAgreementField[field];
                          let access = typeof value === "number" ? AccessLevel[value] : "Disabled";
                          return (
                            <React.Fragment key={field}>
                              <AccessFieldCellLabel>{addSpacesToTitleCase(label)}:</AccessFieldCellLabel>
                              <AccessFieldCellValue>{access}</AccessFieldCellValue>
                            </React.Fragment>
                          );
                        })}
                    </FieldContainer>
                  ) : null}
                </TableCell>
                <TableCell sx={{ pr: 0 }} align="right">
                  <Tooltip title="Remove Permission" enterDelay={1000}>
                    <span>
                      <IconButton onClick={() => handleRemoveClicked(pendingChange)}>
                        <CloseIcon color="error" sx={{ fontSize: "1.2em" }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <ButtonContainer>
        <CancelButton variant="text" onClick={() => closeDialog()}>
          Cancel
        </CancelButton>
        <LoadingButton
          variant="contained"
          color="primary"
          startIcon={<SaveIcon />}
          loading={isSaving}
          disabled={isSaving || !pendingChanges.length}
          onClick={saveChanges}
        >
          Save
        </LoadingButton>
      </ButtonContainer>
    </React.Fragment>
  );
}
